import React from 'react';
import { useApp } from '@/contexts/AppContext';
import ProgressiveReduction from '@/components/apex/ProgressiveReduction';
import GovernanceStatement from '@/components/apex/GovernanceStatement';
import { useNavigate } from 'react-router-dom';
import AnimatedCounter from '@/components/apex/AnimatedCounter';
import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import InvestigationBadge from '@/components/apex/InvestigationBadge';
import RecordDrawer from '@/components/apex/RecordDrawer';
import useListDrawer from '@/hooks/useListDrawer';

const TILE_COLOR = {
  lens: 'border-apex-lens text-apex-lens',
  neutral: 'border-slate-300 text-apex-neutral',
  warning: 'border-apex-warning text-apex-warning',
  critical: 'border-apex-critical text-apex-critical',
};

export default function ContainerGovernance() {
  const { seed } = useApp();
  const navigate = useNavigate();
  const items = seed?.container_governance_actions ?? [];
  const { active, setActive, openPrev, openNext, index, total } = useListDrawer({ items });

  if (!seed) return null;

  const buildRecord = (a) => {
    const tone = a.severity === 'critical' ? 'critical' : a.severity === 'high' ? 'warning' : 'neutral';
    return {
      idTag: a.id,
      title: a.statement,
      severity: a.severity,
      subtitle: 'Container governance action · APEX correlated this from ECR scans + CloudTrail + IAM context',
      verdict: {
        tone,
        label: a.severity === 'critical' ? 'Supply-chain trust break' : a.severity === 'high' ? 'Unapproved artifact in production' : 'Hardening action available',
        sub: a.detail || 'A container artifact in production cannot be traced to an approved build lineage. Until it is replaced or re-approved, every upstream change becomes a downstream exposure.',
      },
      signal: {
        tone,
        lead: 'Why APEX surfaced this:',
        body: 'Container governance failures are not noisy CVEs — they are trust-boundary leaks. A single dev-origin image in production means the production pipeline accepted an artifact it should never have seen. Closing the artifact is fast; closing the path is what removes the class of risk.',
      },
      meta: [
        { label: 'CloudTrail evidence', value: `${a.evidence_count}`, mono: true, sub: `Events correlated for ${a.id}` },
        { label: 'Framework controls', value: a.controls ? `${a.controls}` : '—', mono: true, sub: a.controls ? 'Mapped to compliance baselines' : 'Not mapped' },
        { label: 'Severity tier', value: a.severity, sub: 'APEX-classified', tone },
        { label: 'Provenance proof status', value: 'PROVEN', tone: 'success', mono: true, sub: 'Traced via CloudTrail ECR PutImage events · pipeline correlation' },
        { label: 'Provenance depth', value: a.dev_origin ? '8.8 / 10' : '9.3 / 10', tone: a.dev_origin ? 'warning' : 'success', mono: true, sub: a.dev_origin ? 'CloudTrail + ECR + Task Definition history' : 'CloudTrail + ECR + GitHub Actions + Task Definition' },
        { label: 'Linked investigation', value: a.investigation ?? 'Not linked', mono: true, tone: a.investigation ? 'critical' : '', sub: a.investigation ? 'Active CISO inquiry · click to open' : 'No open inquiry', pivot: a.investigation ? { to: `/lens/investigations?id=${a.investigation}`, label: `Open ${a.investigation}` } : undefined },
      ],
      sections: [
        {
          title: 'Image origin in production',
          type: 'chart',
          chart: {
            kind: 'donut',
            centerValue: '127K',
            centerSub: 'images scanned',
            segments: [
              { label: 'Approved · prod pipeline', value: 62, color: '#10B981' },
              { label: 'Approved · trusted upstream', value: 24, color: '#0EA5E9' },
              { label: 'Unverified provenance', value: 9, color: '#F59E0B' },
              { label: 'Dev-origin in production', value: 5, color: '#DC2626' },
            ],
          },
        },
        {
          title: 'Image lineage',
          type: 'chart',
          chart: {
            kind: 'progress',
            steps: [
              { label: 'Base image',        sub: 'node:16-alpine · EOL Apr 2024',                state: 'done' },
              { label: 'Shared layer',      sub: 'stratum-base-layer:2.1',                       state: 'done' },
              { label: 'App image built',   sub: 'dev-sandbox · developer-console-access',       state: 'done' },
              { label: 'Pushed to ECR',     sub: 'Nov 28, 2025 · mutable :latest tag',           state: 'done' },
              { label: 'Deployed to prod',  sub: 'Feb 14, 2026 · NO pipeline event',             state: 'active' },
            ],
          },
        },
        {
          title: 'Affected business services',
          type: 'kv',
          items: [
            { k: 'Stratum Payments Gateway',  v: 'PCI in scope · CRITICAL',  tone: 'critical', pivot: { to: '/lens/accounts/stratum-payments-prod', label: 'Open Stratum Payments account' } },
            { k: 'Stratum Client Reporting',  v: 'DORA in scope · HIGH',     tone: 'warning',  pivot: { to: '/lens/accounts/stratum-reporting-prod', label: 'Open Stratum Reporting account' } },
            { k: 'Stratum Customer API',      v: 'SOC 2 in scope · HIGH',    tone: 'warning',  pivot: { to: '/lens/accounts/stratum-banking-prod', label: 'Open Stratum Customer API account' } },
          ],
        },
        {
          title: 'Operational signals',
          type: 'kpis',
          kpis: [
            { label: 'Reachability', value: a.severity === 'critical' ? 'Tier 0' : a.severity === 'high' ? 'Tier 1' : 'Tier 2', sub: 'Resources accessible from this artifact', tone: a.severity === 'critical' ? 'critical' : 'warning', delta: 'flat' },
            { label: 'Recurrence risk', value: 'Open', sub: 'No SCP / Terraform guardrail in place', tone: 'warning', delta: 'up' },
            { label: 'Effort to close', value: '20–40 min', sub: 'Replace + add guardrail', tone: 'lens', delta: 'down' },
          ],
        },
        {
          title: 'Recommended remediation',
          type: 'list',
          items: [
            'Pull the image from a production-only ECR repository or replace it with the production-pipeline build.',
            'Sever any cross-environment trust path that allowed the dev-origin artifact to land in production.',
            'Add a guardrail (Terraform / SCP / OPA policy) so this class of drift cannot recur silently.',
          ],
        },
      ],
      actions: [
        {
          label: a.investigation ? `Open ${a.investigation}` : 'Open INV-2026-042',
          primary: true,
          testId: 'cga-open-inv',
          onClick: () => navigate(a.investigation ? `/lens/investigations?id=${a.investigation}` : '/lens/investigations?id=INV-2026-042'),
        },
        {
          label: 'Create remediation task',
          testId: 'cga-remediate',
          onClick: () => { navigate('/lens/remediation'); toast.success('Remediation task created', { description: a.id }); },
        },
        {
          label: 'Add to audit bundle',
          testId: 'cga-pin',
          onClick: () => toast.success('Added to audit bundle', { description: `${a.id} pinned to current bundle` }),
        },
      ],
    };
  };

  return (
    <div className="space-y-8" data-testid="container-governance">
      <div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Evidence Engine · Container Governance</span>
        <h1 className="text-2xl font-bold tracking-tight mt-1">From signal to action</h1>
      </div>

      {/* Hero */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-10 text-center">
        <div className="text-[10px] uppercase tracking-[0.2em] text-apex-neutral mb-2">Total Security Hub findings detected</div>
        <div className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tabular text-apex-ink-2 tracking-tight">
          <AnimatedCounter value={127000} duration={1.6} />
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="my-3 flex justify-center">
          <ArrowDown className="w-6 h-6 text-apex-lens" />
        </motion.div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-apex-lens mb-2">Governance actions required</div>
        <div className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tabular text-apex-lens tracking-tight">
          <AnimatedCounter value={9} duration={1.4} />
        </div>
        <p className="mt-4 text-sm text-apex-neutral max-w-2xl mx-auto">
          APEX Lens applied runtime context, environment classification, data sensitivity, and image provenance to eliminate noise. Toggle filters below to see how.
        </p>
      </div>

      {/* Waterfall */}
      <div className="bg-white rounded-xl border border-slate-200 p-5" id="container-waterfall" data-testid="container-waterfall">
        <h3 className="text-sm font-bold text-apex-ink mb-4">Progressive Reduction</h3>
        <ProgressiveReduction steps={seed.container_waterfall} />
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-apex-neutral border-t border-slate-200 pt-4">
          <div className="flex items-center gap-2"><span className="text-apex-success font-mono">~340h</span> triage hours eliminated</div>
          <div className="flex items-center gap-2"><span className="text-apex-success font-mono">~28h</span> engineering time recovered</div>
        </div>
      </div>

      {/* Image classification tiles */}
      <div>
        <h3 className="text-sm font-bold text-apex-ink mb-3">Image Inventory</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {seed.container_image_classes.map((c, i) => (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className={`bg-white rounded-lg border-2 ${TILE_COLOR[c.color]} p-3`}
            >
              <div className="text-2xl font-bold tabular"><AnimatedCounter value={c.count} /></div>
              <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold mt-1">{c.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* The 9 actions */}
      <div>
        <h3 className="text-sm font-bold text-apex-ink mb-3">The 9 Governance Actions</h3>
        <div className="space-y-3">
          {seed.container_governance_actions.map((a) => (
            <GovernanceStatement
              key={a.id}
              testId={`cga-${a.id}`}
              severity={a.severity}
              statement={a.statement}
              detail={a.detail}
              evidenceCount={a.evidence_count}
              controls={a.controls}
              highlighted={a.investigation === 'INV-2026-042'}
              titleSuffix={a.investigation === 'INV-2026-042' ? <InvestigationBadge /> : null}
              onCardClick={() => setActive(a)}
              primaryLabel="View evidence"
              onPrimary={() => a.investigation ? navigate(`/lens/investigations?id=${a.investigation}`) : null}
              secondaryLabel={a.investigation ?? null}
              onSecondary={a.investigation ? () => navigate(`/lens/investigations?id=${a.investigation}`) : null}
            />
          ))}
        </div>
      </div>

      {/* False positive governance */}
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-sm font-bold text-apex-ink mb-2">False Positive Governance</h3>
        <p className="text-xs text-apex-neutral leading-relaxed">14,847 findings suppressed · 23 accepted false positives propagated to 47 accounts · Evidence-backed · Expiry: 90 days</p>
        <button onClick={() => navigate('/lens/audit')} data-testid="cga-manage-suppressions" className="mt-3 text-xs font-semibold text-apex-lens">Manage suppressions →</button>
      </div>

      <RecordDrawer
        open={!!active}
        onOpenChange={(o) => !o && setActive(null)}
        record={active ? buildRecord(active) : null}
        testId="cga-drawer"
        onPrev={openPrev}
        onNext={openNext}
        position={index}
        total={total}
      />
    </div>
  );
}
